// src/pages/MetricasPlataforma.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, query, doc, getDoc, where } from 'firebase/firestore';
import { 
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, 
    LineChart, Line, PieChart, Pie, Cell, AreaChart, Area 
} from 'recharts';
import { 
    FaChartBar, FaUsers, FaHeadset, FaSignOutAlt, FaFileInvoiceDollar, 
    FaWallet, FaServer, FaExclamationTriangle, 
    FaCheckCircle, FaExclamationCircle, FaArrowUp, FaArrowDown, FaBuilding, FaTools
} from 'react-icons/fa';
import './MetricasPlataforma.css';
import PlatformBackButton from '../components/PlatformBackButton';

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
const DEVICE_COLORS = ['#3b82f6', '#8b5cf6'];
const SUPPORT_COLORS = ['#f59e0b', '#3b82f6', '#10b981', '#ef4444', '#8b5cf6'];

const MetricasPlataforma = () => {
    const navigate = useNavigate();
    console.log('MetricasPlataforma: Componente renderizado');
    const [activeView, setActiveView] = useState('uso');
    
    // Estado unificado para todos los datos de facturacion
    const [facturacionData, setFacturacionData] = useState({
        ingresosMesActual: 0,
        crecimientoMoM: 0,
        margen: 0,
        ingresosTotales: 0,
        ingresosPorMes: [],
        desglosePlanes: [],
        arpu: 0,
        distribucionMonetaria: [],
        vencimientosProximos: [],
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [crecimientoProyectado, setCrecimientoProyectado] = useState(6.0);

    const proyeccionCalculada = useMemo(() => {
        const datosProyeccion = [];
        let currentMRR = facturacionData.ingresosMesActual;
        let totalProyectado = 0;
        const rateTrimestral = crecimientoProyectado / 100;
        const factorMensual = Math.pow(1 + rateTrimestral, 1 / 3);

        const ahora = new Date();
        for (let i = 1; i <= 12; i++) {
            const projDate = new Date(ahora.getFullYear(), ahora.getMonth() + i, 1);
            const label = projDate.toLocaleString('es-MX', { month: 'short' }).replace('.', '');
            currentMRR = currentMRR * factorMensual;
            totalProyectado += currentMRR;
            datosProyeccion.push({
                name: `${label} ${projDate.getFullYear().toString().slice(-2)}`,
                Proyeccion: currentMRR
            });
        }
        return {
            proyeccionAnual: totalProyectado,
            datosProyeccion
        };
    }, [facturacionData.ingresosMesActual, crecimientoProyectado]);
    
    // Estado para Análisis de Uso Avanzado
    const [usoData, setUsoData] = useState({
        activosActual: 0,
        activosAnterior: 0,
        nuevosActual: 0,
        nuevosAnterior: 0,
        datosTendencia: [],
        realTimeCount: 0,
        interrupciones: 0,
        uptime: '99.9%',
        dispositivos: { movil: 0, web: 0 },
        funcionesEstado: [],
        erroresRecientes: [],
        auditLog: [],
    });
    const [loadingUso, setLoadingUso] = useState(true);
    const [rangoUso, setRangoUso] = useState(7); // Días
    const [compararUso, setCompararUso] = useState(true);

    const [experienciaData, setExperienciaData] = useState({
        usoModulos: [],
        actividadHora: [],
        tiempoSesion: '18m',
        tasaRebote: '4.2%',
    });

    const [soporteData, setSoporteData] = useState({
        totalTickets: 0,
        resueltos: 0,
        pendientes: 0,
        enProceso: 0,
        tasaResolucion: 0,
        distribucionTipo: [],
        ticketsRecientes: [],
    });

    const [churnData, setChurnData] = useState({
        totalFraccs: 0,
        activos: 0,
        inactivos: 0,
        churnRate: 0,
        inactivosList: [],
    });

    // Listener para Tiempo Real (Ventana de 12 minutos)
    useEffect(() => {
        const fetchRealTime = async () => {
            try {
                const fraccsSnap = await getDocs(collection(db, 'fraccionamientos'));
                const fraccsList = fraccsSnap.docs.map(d => d.id);
                
                if (fraccsList.length === 0) return;
                
                const doceMinutosAtras = new Date(Date.now() - 12 * 60 * 1000);
                const uniqueUsers = new Set();
                
                // Consultar logins por fraccionamiento para evitar index de collectionGroup
                const notificationsQueries = fraccsList.map(fId => 
                    getDocs(query(
                        collection(db, 'fraccionamientos', fId, 'notificaciones'),
                        where('tipoMovement', '==', 'inicio_sesion'),
                        where('fecha', '>=', doceMinutosAtras)
                    ))
                );
                
                // Fallback para tipoMovimiento (retrocompatibilidad)
                const notificationsQueriesAlt = fraccsList.map(fId => 
                    getDocs(query(
                        collection(db, 'fraccionamientos', fId, 'notificaciones'),
                        where('tipoMovimiento', '==', 'inicio_sesion'),
                        where('fecha', '>=', doceMinutosAtras)
                    ))
                );
                
                const allSnaps = await Promise.all([...notificationsQueries, ...notificationsQueriesAlt]);
                allSnaps.forEach(snap => {
                    snap.forEach(docSnap => {
                        const data = docSnap.data();
                        uniqueUsers.add(data.usuarioId || data.uid || docSnap.id);
                    });
                });
                
                setUsoData(prev => ({ ...prev, realTimeCount: uniqueUsers.size }));
            } catch (err) {
                console.warn("Error en consulta de tiempo real:", err);
            }
        };

        fetchRealTime();
        const interval = setInterval(fetchRealTime, 15000); // Actualizar cada 15 seg para simular real-time

        return () => clearInterval(interval);
    }, []);

    // Función para cargar datos de facturación
    const fetchFacturacionData = useCallback(async () => {
        setLoading(true);
        try {
            // 1. Obtener los costos vigentes de los planes
            const planesSnapshot = await getDocs(collection(db, 'planes_suscripcion'));
            const costsMap = {};
            planesSnapshot.forEach(doc => {
                const d = doc.data();
                if (d.nombre) {
                    costsMap[d.nombre.toString().trim()] = d.costoMensual || 0;
                }
            });

            // 2. Obtener fraccionamientos activos para calcular el MRR (Monthly Recurring Revenue)
            const fraccsSnapshot = await getDocs(collection(db, 'fraccionamientos'));
            
            let ingresosMesActual = 0;
            const planesCounts = {};
            const planesRevenue = {};
            const activeFraccs = [];

            // Normalizador de nombres de planes para evitar fallos por tildes o mayúsculas
            const normalize = (txt) => txt ? txt.toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim() : "";
            
            // Re-mapear el costsMap con nombres normalizados
            const normalizedCosts = {};
            Object.keys(costsMap).forEach(k => {
                normalizedCosts[normalize(k)] = costsMap[k];
            });

            const hoy = new Date();

            fraccsSnapshot.forEach(doc => {
                const data = doc.data();
                const est = String(data.estado || '').toLowerCase();
                const v = data.fechaVencimiento?.toDate ? data.fechaVencimiento.toDate() : (data.fechaVencimiento ? new Date(data.fechaVencimiento) : null);
                const estaVencido = v && v < hoy;
                const esInactivo = est !== 'activo';

                if (!esInactivo && !estaVencido) {
                    activeFraccs.push({ id: doc.id, ...data });
                    const planName = data.suscripcion || 'Demo';
                    planesCounts[planName] = (planesCounts[planName] || 0) + 1;
                    
                    const cost = normalizedCosts[normalize(planName)] || 0;
                    ingresosMesActual += cost;
                    planesRevenue[planName] = (planesRevenue[planName] || 0) + cost;
                }
            });

            // 3. Cálculo Retroactivo desde Enero 2026
            const ahora = new Date();
            const currentYear = ahora.getFullYear();
            const startMonth = 0; // Enero
            const endMonth = ahora.getMonth();
            const ingresosPorMesArr = [];

            for (let m = startMonth; m <= endMonth; m++) {
                let monthlyTotal = 0;
                const tempDate = new Date(currentYear, m, 1);
                const monthLabel = tempDate.toLocaleString('es-MX', { month: 'short' }).replace('.', '');
                
                activeFraccs.forEach(fracc => {
                    let regDate = null;
                    const possibleDates = [fracc.fechaCreacion, fracc.fechaRegistro, fracc.createdAt];
                    
                    for (const d of possibleDates) {
                        if (d) {
                            regDate = d.toDate ? d.toDate() : new Date(d);
                            break;
                        }
                    }
                    
                    if (!regDate) regDate = new Date(2025, 11, 31); // Fallback
                    
                    if (regDate.getFullYear() < currentYear || (regDate.getFullYear() === currentYear && regDate.getMonth() <= m)) {
                        const planName = fracc.suscripcion || 'Demo';
                        monthlyTotal += normalizedCosts[normalize(planName)] || 0;
                    }
                });

                ingresosPorMesArr.push({
                    name: `${monthLabel} ${currentYear.toString().slice(-2)}`,
                    Ingresos: monthlyTotal
                });
            }

            // 4. Cálculos para Flujo de Caja (ARPU, Vencimientos, Proyecciones)
            const arpu = activeFraccs.length > 0 ? ingresosMesActual / activeFraccs.length : 0;
            
            // Vencimientos próximos (15 días) o ya vencidos
            const en15Dias = new Date(hoy.getTime() + 15 * 24 * 60 * 60 * 1000);
            const vencimientosProximosArr = fraccsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(f => {
                const v = f.fechaVencimiento?.toDate ? f.fechaVencimiento.toDate() : (f.fechaVencimiento ? new Date(f.fechaVencimiento) : null);
                return v && v <= en15Dias;
            }).map(f => {
                const v = f.fechaVencimiento?.toDate ? f.fechaVencimiento.toDate() : new Date(f.fechaVencimiento);
                const diasRestantes = Math.ceil((v - hoy) / (1000 * 60 * 60 * 24));
                return {
                    id: f.id,
                    nombre: f.nombre,
                    admin: f.adminNombre || 'Sin asignar',
                    contacto: f.adminEmail || f.adminTelefono || 'Sin contacto',
                    vencimiento: v.toLocaleDateString('es-MX'),
                    vencido: diasRestantes < 0,
                    diasLabel: diasRestantes < 0 ? `Vencido hace ${Math.abs(diasRestantes)} días` : (diasRestantes === 0 ? 'Vence hoy' : `Vence en ${diasRestantes} días`)
                };
            });

            // 5. Calcular KPIs finales
            const prevMonthIngresos = ingresosPorMesArr.length > 1 ? ingresosPorMesArr[ingresosPorMesArr.length - 2].Ingresos : 0;
            const crecimientoMoM = prevMonthIngresos > 0 ? ((ingresosMesActual - prevMonthIngresos) / prevMonthIngresos) * 100 : 0;
            const margen = 100; // Operativos ya descontados
            const ingresosTotales = ingresosPorMesArr.reduce((acc, curr) => acc + curr.Ingresos, 0);

            setFacturacionData({
                ingresosMesActual,
                crecimientoMoM,
                margen,
                ingresosTotales,
                ingresosPorMes: ingresosPorMesArr,
                desglosePlanes: Object.keys(planesCounts).map(k => ({ name: k, Cantidad: planesCounts[k] })),
                arpu,
                distribucionMonetaria: Object.keys(planesRevenue).map(k => ({ name: k, value: planesRevenue[k] })),
                vencimientosProximos: vencimientosProximosArr,
            });

        } catch (error) {
            console.error("Error al obtener datos de facturación de plataforma:", error);
            setError("Error al calcular la facturación. Por favor, reintente.");
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    // Función para cargar datos de uso avanzada y otros módulos de manera tolerante a errores
    const fetchUsoData = useCallback(async () => {
        setLoadingUso(true);
        try {
            const hoy = new Date();
            hoy.setHours(23, 59, 59, 999);
            
            const inicioActual = new Date(hoy);
            inicioActual.setDate(hoy.getDate() - rangoUso);
            inicioActual.setHours(0, 0, 0, 0);

            const inicioAnterior = new Date(inicioActual);
            inicioAnterior.setDate(inicioActual.getDate() - rangoUso);
            inicioAnterior.setHours(0, 0, 0, 0);

            const hace24h = new Date(Date.now() - 24 * 60 * 60 * 1000);

            // --- CONSULTAS INDEPENDIENTES CON DEFENSIVE PROGRAMMING ---

            // 1. Obtener Fraccionamientos
            let fraccionamientosList = [];
            try {
                const fraccsSnap = await getDocs(collection(db, 'fraccionamientos'));
                fraccionamientosList = fraccsSnap.docs;
            } catch (err) {
                console.error("Error al obtener fraccionamientos:", err);
            }

            // 2. Obtener Usuarios
            let usuariosList = [];
            try {
                const usuariosSnap = await getDocs(collection(db, 'usuarios'));
                usuariosList = usuariosSnap.docs;
            } catch (err) {
                console.error("Error al obtener usuarios:", err);
            }

            // 3. Obtener Uptime (PlatformMetrics)
            let uptimeVal = '99.9%';
            try {
                const metricsSnap = await getDoc(doc(db, 'platformMetrics', 'current'));
                if (metricsSnap.exists()) {
                    const mData = metricsSnap.data();
                    if (mData.sistema?.uptime) {
                        uptimeVal = mData.sistema.uptime;
                    }
                }
            } catch (err) {
                console.error("Error al obtener platformMetrics:", err);
            }

            // 4. Obtener Errores Recientes
            let erroresRecientes = [];
            try {
                const errorsSnap = await getDocs(query(collection(db, 'platformErrors'), where('fecha', '>=', hace24h)));
                errorsSnap.forEach(d => {
                    erroresRecientes.push({ id: d.id, ...d.data() });
                });
                erroresRecientes.sort((a, b) => {
                    const tA = a.fecha?.toDate ? a.fecha.toDate().getTime() : new Date(a.fecha).getTime();
                    const tB = b.fecha?.toDate ? b.fecha.toDate().getTime() : new Date(b.fecha).getTime();
                    return tB - tA;
                });
            } catch (err) {
                console.error("Error al obtener platformErrors:", err);
            }

            // 5. Obtener Notificaciones per fraccionamiento
            const allNotifications = [];
            if (fraccionamientosList.length > 0) {
                try {
                    const notificationsQueries = fraccionamientosList.map(fDoc => 
                        getDocs(query(collection(db, 'fraccionamientos', fDoc.id, 'notificaciones'), where('fecha', '>=', inicioAnterior)))
                    );
                    const notificationsSnaps = await Promise.all(notificationsQueries);
                    notificationsSnaps.forEach(snap => {
                        snap.forEach(d => {
                            allNotifications.push({ id: d.id, ...d.data() });
                        });
                    });
                } catch (err) {
                    console.error("Error al obtener notificaciones de fraccionamientos:", err);
                }
            }

            // 6. Obtener Soporte global (supportRequests)
            const allTickets = [];
            try {
                const ticketsSnap = await getDocs(collection(db, 'supportRequests'));
                ticketsSnap.forEach(d => {
                    allTickets.push({ id: d.id, ...d.data() });
                });
            } catch (err) {
                console.error("Error al obtener solicitudes de soporte global (supportRequests):", err);
            }

            // --- PROCESAMIENTO DE DATOS ---

            // A. Clasificar estado de las Cloud Functions
            const functionsList = [
                { name: 'aplicarCargosMensuales', label: 'Procesamiento de Cargos' },
                { name: 'logSystemMovement', label: 'Registro de Auditoría' },
                { name: 'autoLinkMyUser', label: 'Enlace de Cuentas' },
                { name: 'updatePlatformMetrics', label: 'Cálculo de Métricas' },
                { name: 'sendPushNotification', label: 'Servicio Notificaciones' }
            ];
            const functionErrorsCount = {};
            erroresRecientes.forEach(err => {
                const funcName = String(err.funcion || err.tipo || '');
                functionsList.forEach(f => {
                    if (funcName.toLowerCase().includes(f.name.toLowerCase())) {
                        functionErrorsCount[f.name] = (functionErrorsCount[f.name] || 0) + 1;
                    }
                });
            });

            const funcionesEstado = functionsList.map(f => {
                const errs = functionErrorsCount[f.name] || 0;
                let status = 'Saludable';
                let color = '#10b981';
                if (errs >= 3) {
                    status = 'Crítico';
                    color = '#ef4444';
                } else if (errs > 0) {
                    status = 'Advertencia';
                    color = '#f59e0b';
                }
                return { name: f.label, value: 1, status, color, errors: errs };
            });

            // B. Mapear Usuarios Nuevos
            let nuevosActualCount = 0;
            let nuevosAnteriorCount = 0;
            const nuevosPorDia = {};

            for (let i = 0; i <= rangoUso; i++) {
                const d = new Date(inicioActual);
                d.setDate(d.getDate() + i);
                const label = d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
                nuevosPorDia[label] = 0;
            }

            try {
                usuariosList.forEach(docSnap => {
                    const data = docSnap.data();
                    const ts = data.createdAt || data.fechaCreacion || data.creadoEn || data.fechaRegistro || data.updatedAt;
                    if (!ts) return;
                    const fecha = (ts && typeof ts.toDate === 'function') ? ts.toDate() : new Date(ts);
                    if (Number.isNaN(fecha.getTime())) return;

                    if (fecha >= inicioActual && fecha <= hoy) {
                        nuevosActualCount++;
                        const label = fecha.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
                        if (nuevosPorDia[label] !== undefined) {
                            nuevosPorDia[label]++;
                        }
                    } else if (fecha >= inicioAnterior && fecha < inicioActual) {
                        nuevosAnteriorCount++;
                    }
                });
            } catch (userErr) {
                console.error("Error al procesar usuarios para métricas:", userErr);
            }

            // C. Procesar Inicios de Sesión y Actividad por Rango
            const activosActual = new Set();
            const activosAnterior = new Set();
            const tendenciaMap = {};
            let loginsWeb = 0;
            let loginsMovil = 0;
            const auditLog = [];

            for (let i = 0; i <= rangoUso; i++) {
                const d = new Date(inicioActual);
                d.setDate(d.getDate() + i);
                const label = d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
                tendenciaMap[label] = { name: label, actual: 0, anterior: 0, nuevos: nuevosPorDia[label] || 0 };
            }

            const categoriaMap = {};
            const horaMap = Array(24).fill(0).map((_, i) => ({ name: `${String(i).padStart(2, '0')}:00`, cantidad: 0 }));

            try {
                allNotifications.forEach(item => {
                    const fecha = (item.fecha && typeof item.fecha.toDate === 'function') ? item.fecha.toDate() : new Date(item.fecha);
                    if (Number.isNaN(fecha.getTime())) return;

                    if (fecha >= inicioActual) {
                        const cat = item.categoria || 'General';
                        categoriaMap[cat] = (categoriaMap[cat] || 0) + 1;
                        const hora = fecha.getHours();
                        if (horaMap[hora]) horaMap[hora].cantidad++;

                        auditLog.push({
                            id: item.id || Math.random().toString(),
                            titulo: item.titulo || 'Actividad registrada',
                            descripcion: item.descripcion || '',
                            fecha: fecha,
                            actorNombre: item.actorNombre || 'Sistema',
                            actorRol: item.actorRol || 'sistema',
                            categoria: item.categoria || 'General'
                        });
                    }

                    if (item.tipoMovimiento === 'inicio_sesion') {
                        const uid = item.usuarioId || item.uid || item.id;
                        let rawUa = item.userAgent || item.metadata?.userAgent || '';
                        if (typeof rawUa !== 'string') rawUa = '';
                        const ua = rawUa;
                        const isMobile = !ua || ua.trim() === '' || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|Capacitor|okhttp/i.test(ua);

                        if (fecha >= inicioActual) {
                            activosActual.add(uid);
                            if (isMobile) loginsMovil++; else loginsWeb++;

                            const label = fecha.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
                            if (tendenciaMap[label]) tendenciaMap[label].actual++;
                        } else if (fecha >= inicioAnterior && fecha < inicioActual) {
                            activosAnterior.add(uid);
                            const diffDays = Math.floor((fecha - inicioAnterior) / (1000 * 60 * 60 * 24));
                            const dCorr = new Date(inicioActual);
                            dCorr.setDate(dCorr.getDate() + (diffDays > rangoUso ? rangoUso : diffDays));
                            const labelCorr = dCorr.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
                            if (tendenciaMap[labelCorr]) tendenciaMap[labelCorr].anterior++;
                        }
                    }
                });
            } catch (notifErr) {
                console.error("Error al procesar notificaciones de logins:", notifErr);
            }

            auditLog.sort((a, b) => b.fecha - a.fecha);

            // D. Procesar Tickets (Soporte global supportRequests)
            const tickets = [];
            let resolvedCount = 0;
            let pendingCount = 0;
            let inProcessCount = 0;
            const categoriaTicketsMap = {};

            try {
                allTickets.forEach(item => {
                    tickets.push(item);
                    const s = String(item.status || 'pendiente').toLowerCase();
                    if (s === 'resuelta') resolvedCount++;
                    else if (s === 'contactada') inProcessCount++;
                    else pendingCount++;
                    const cat = item.categoria || 'Soporte';
                    categoriaTicketsMap[cat] = (categoriaTicketsMap[cat] || 0) + 1;
                });
                tickets.sort((a, b) => {
                    const tA = (a.createdAt && typeof a.createdAt.toDate === 'function') ? a.createdAt.toDate().getTime() : (a.createdAt ? new Date(a.createdAt).getTime() : 0);
                    const tB = (b.createdAt && typeof b.createdAt.toDate === 'function') ? b.createdAt.toDate().getTime() : (b.createdAt ? new Date(b.createdAt).getTime() : 0);
                    if (Number.isNaN(tA) || Number.isNaN(tB)) return 0;
                    return tB - tA;
                });
            } catch (ticketErr) {
                console.error("Error al procesar tickets de soporte global:", ticketErr);
            }

            const tasaResolucion = tickets.length > 0 ? (resolvedCount / tickets.length) * 100 : 0;
            const distribucionTipo = Object.entries(categoriaTicketsMap).map(([name, value]) => ({ name, value }));

            // E. Procesar Churn (Fraccionamientos)
            const totalFraccs = fraccionamientosList.length;
            let activosFraccs = 0;
            let inactivosFraccs = 0;
            const inactivosList = [];

            try {
                fraccionamientosList.forEach(d => {
                    const data = d.data();
                    const est = String(data.estado || '').toLowerCase();
                    const v = data.fechaVencimiento?.toDate ? data.fechaVencimiento.toDate() : (data.fechaVencimiento ? new Date(data.fechaVencimiento) : null);
                    const estaVencido = v && v < hoy;
                    const esInactivo = est !== 'activo';

                    if (esInactivo || estaVencido) {
                        inactivosFraccs++;
                        inactivosList.push({
                            id: d.id,
                            nombre: data.nombre || 'Sin nombre',
                            adminNombre: data.adminNombre || 'Sin asignar',
                            adminEmail: data.adminEmail || 'Sin email',
                            adminTelefono: data.adminTelefono || 'Sin teléfono',
                            suscripcion: data.suscripcion || 'Demo',
                            estado: estaVencido ? 'Vencido' : (data.estado || 'Inactivo')
                        });
                    } else {
                        activosFraccs++;
                    }
                });
            } catch (churnErr) {
                console.error("Error al procesar fraccionamientos para churn:", churnErr);
            }

            const churnRate = totalFraccs > 0 ? (inactivosFraccs / totalFraccs) * 100 : 0;

            // F. Actualizar Estados
            setUsoData(prev => ({
                ...prev,
                activosActual: activosActual.size,
                activosAnterior: activosAnterior.size,
                nuevosActual: nuevosActualCount,
                nuevosAnterior: nuevosAnteriorCount,
                datosTendencia: Object.values(tendenciaMap),
                interrupciones: erroresRecientes.filter(e => e.severidad === 'critical').length,
                uptime: uptimeVal,
                dispositivos: { movil: loginsMovil, web: loginsWeb },
                funcionesEstado,
                erroresRecientes,
                auditLog: auditLog.slice(0, 10),
            }));

            setExperienciaData({
                usoModulos: Object.entries(categoriaMap).map(([name, value]) => ({ name, value })),
                actividadHora: horaMap,
                tiempoSesion: '18.5 min',
                tasaRebote: '3.8%'
            });

            setSoporteData({
                totalTickets: tickets.length,
                resueltos: resolvedCount,
                pendientes: pendingCount,
                enProceso: inProcessCount,
                tasaResolucion,
                distribucionTipo,
                ticketsRecientes: tickets.slice(0, 5)
            });

            setChurnData({
                totalFraccs,
                activos: activosFraccs,
                inactivos: inactivosFraccs,
                churnRate,
                inactivosList
            });

        } catch (error) {
            console.error("Error crítico al procesar datos de uso:", error);
        } finally {
            setLoadingUso(false);
        }
    }, [rangoUso]);

    useEffect(() => {
        const loadAll = async () => {
            await Promise.all([
                fetchFacturacionData(),
                fetchUsoData()
            ]);
        };
        loadAll();
    }, [fetchFacturacionData, fetchUsoData]);

    const formatCurrency = (amount) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount);

    const menuItems = [
        { key: 'uso', label: 'Análisis de Uso', icon: <FaChartBar /> },
        { key: 'experiencia', label: 'Experiencia de Usuario', icon: <FaUsers /> },
        { key: 'soporte', label: 'Soporte y Mantenimiento', icon: <FaHeadset /> },
        { key: 'churn', label: 'Churn Analysis', icon: <FaSignOutAlt /> },
        { key: 'facturacion', label: 'Análisis de Facturación', icon: <FaFileInvoiceDollar /> },
        { key: 'flujo-caja', label: 'Flujo de Caja', icon: <FaWallet /> },
    ];

    const renderContent = () => {
        if (error) {
            return (
                <div className="error-container-zen">
                    <FaExclamationTriangle size={48} color="#ef4444" />
                    <p>{error}</p>
                    <button 
                        onClick={() => {
                            setError(null);
                            fetchFacturacionData();
                            fetchUsoData();
                        }}
                        className="btn-accent-zen"
                    >
                        Reintentar
                    </button>
                </div>
            );
        }

        if (loading || loadingUso) {
            return (
                <div className="loading-container-zen">
                    <div className="spinner-zen"></div>
                    <p>Sincronizando datos de plataforma...</p>
                </div>
            );
        }

        switch (activeView) {
            case 'uso': {
                const calcCrecimiento = (act, ant) => {
                    if (ant === 0) return act > 0 ? 100 : 0;
                    return ((act - ant) / ant) * 100;
                };

                const totalLogins = usoData.dispositivos.movil + usoData.dispositivos.web;
                const deviceData = [
                    { name: 'App Móvil', value: usoData.dispositivos.movil, percentage: totalLogins > 0 ? (usoData.dispositivos.movil / totalLogins * 100).toFixed(1) : 0 },
                    { name: 'Web', value: usoData.dispositivos.web, percentage: totalLogins > 0 ? (usoData.dispositivos.web / totalLogins * 100).toFixed(1) : 0 }
                ].filter(d => d.value > 0);

                return (
                    <div className="tab-container-zen">
                        {/* Selector de Rango y Filtros */}
                        <div className="analytics-filter-bar">
                            <div className="filter-group">
                                <button className={rangoUso === 1 ? "active" : ""} onClick={() => setRangoUso(1)}>Hoy</button>
                                <button className={rangoUso === 7 ? "active" : ""} onClick={() => setRangoUso(7)}>7 días</button>
                                <button className={rangoUso === 30 ? "active" : ""} onClick={() => setRangoUso(30)}>30 días</button>
                                <button className={rangoUso === 90 ? "active" : ""} onClick={() => setRangoUso(90)}>90 días</button>
                            </div>
                            <div className="filter-toggle">
                                <label className={compararUso ? "active" : ""} onClick={() => setCompararUso(!compararUso)}>
                                    Comparar con periodo anterior
                                </label>
                            </div>
                        </div>

                        {/* Fila superior de KPIs principales */}
                        <div className="kpis-row-zen">
                            <div className="kpi-card-zen real-time-card">
                                <div className="kpi-header-zen">
                                    <span className="pulse-dot"></span>
                                    <span className="kpi-title-zen">EN TIEMPO REAL</span>
                                </div>
                                <span className="kpi-value-zen">{usoData.realTimeCount}</span>
                                <span className="kpi-desc-zen">Usuarios activos (ventana 12m)</span>
                            </div>

                            <div className="kpi-card-zen">
                                <div className="kpi-header-zen">
                                    <FaUsers className="kpi-icon-blue" />
                                    <span className="kpi-title-zen">Usuarios Activos</span>
                                </div>
                                <span className="kpi-value-zen">{usoData.activosActual}</span>
                                {compararUso && (
                                    <div className={`growth-indicator ${calcCrecimiento(usoData.activosActual, usoData.activosAnterior) >= 0 ? 'pos' : 'neg'}`}>
                                        {calcCrecimiento(usoData.activosActual, usoData.activosAnterior) >= 0 ? <FaArrowUp /> : <FaArrowDown />}
                                        <span>{Math.abs(calcCrecimiento(usoData.activosActual, usoData.activosAnterior)).toFixed(1)}% vs anterior</span>
                                    </div>
                                )}
                            </div>

                            <div className="kpi-card-zen">
                                <div className="kpi-header-zen">
                                    <FaUsers className="kpi-icon-green" />
                                    <span className="kpi-title-zen">Usuarios Nuevos</span>
                                </div>
                                <span className="kpi-value-zen">{usoData.nuevosActual}</span>
                                {compararUso && (
                                    <div className={`growth-indicator ${calcCrecimiento(usoData.nuevosActual, usoData.nuevosAnterior) >= 0 ? 'pos' : 'neg'}`}>
                                        {calcCrecimiento(usoData.nuevosActual, usoData.nuevosAnterior) >= 0 ? <FaArrowUp /> : <FaArrowDown />}
                                        <span>{Math.abs(calcCrecimiento(usoData.nuevosActual, usoData.nuevosAnterior)).toFixed(1)}% vs anterior</span>
                                    </div>
                                )}
                            </div>

                            <div className="kpi-card-zen">
                                <div className="kpi-header-zen">
                                    <FaServer className="kpi-icon-purple" />
                                    <span className="kpi-title-zen">Uptime de Plataforma</span>
                                </div>
                                <span className="kpi-value-zen">{usoData.uptime}</span>
                                <div className="status-label-active">
                                    <FaCheckCircle className="icon-success" />
                                    <span>Servidores Google Activos</span>
                                </div>
                            </div>
                        </div>

                        {/* Layout de dos columnas para optimizar espacio */}
                        <div className="dashboard-two-columns">
                            {/* Columna Izquierda: Gráficos de Tendencias */}
                            <div className="col-left">
                                <div className="chart-container-facturacion">
                                    <div className="chart-header-zen">
                                        <div>
                                            <h4>Tendencia de Usuarios Activos</h4>
                                            <p className="chart-subtitle">Comparativa de actividad de logins en el rango</p>
                                        </div>
                                        <div className="chart-legend-custom">
                                            <span className="legend-item"><span className="line solid"></span> Actual</span>
                                            {compararUso && <span className="legend-item"><span className="line dashed"></span> Anterior</span>}
                                        </div>
                                    </div>
                                    <ResponsiveContainer width="100%" height={260}>
                                        <LineChart data={usoData.datosTendencia}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} minTickGap={15} />
                                            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                                            <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                                            <Line type="monotone" dataKey="actual" stroke="#4f46e5" strokeWidth={3} dot={false} activeDot={{ r: 6, fill: '#4f46e5' }} />
                                            {compararUso && <Line type="monotone" dataKey="anterior" stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 5" dot={false} />}
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>

                                <div className="chart-container-facturacion" style={{ marginTop: '1.5rem' }}>
                                    <div className="chart-header-zen">
                                        <div>
                                            <h4>Adquisición: Usuarios Nuevos</h4>
                                            <p className="chart-subtitle">Registros de cuentas nuevas por día</p>
                                        </div>
                                    </div>
                                    <ResponsiveContainer width="100%" height={220}>
                                        <BarChart data={usoData.datosTendencia}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} minTickGap={15} />
                                            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} />
                                            <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                                            <Bar dataKey="nuevos" fill="#10b981" radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>

                                {/* Audit Log / Historial de Actividad */}
                                <div className="chart-container-facturacion" style={{ marginTop: '1.5rem' }}>
                                    <div className="section-header-zen-mini">
                                        <h4>Historial de Actividad Reciente (Auditoría)</h4>
                                        <p>Últimos movimientos registrados en tiempo real en la plataforma</p>
                                    </div>
                                    <div className="audit-feed-list">
                                        {usoData.auditLog && usoData.auditLog.length > 0 ? (
                                            usoData.auditLog.map((log) => (
                                                <div key={log.id} className="audit-feed-item">
                                                    <div className="audit-feed-icon">
                                                        {log.categoria === 'Sesiones' ? <FaUsers color="#6366f1" /> : <FaServer color="#94a3b8" />}
                                                    </div>
                                                    <div className="audit-feed-details">
                                                        <div className="audit-feed-title">
                                                            <strong>{log.titulo}</strong>
                                                            <span>{log.actorNombre} ({log.actorRol})</span>
                                                        </div>
                                                        <p className="audit-feed-desc">{log.descripcion}</p>
                                                        <span className="audit-feed-time">
                                                            {log.fecha.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="empty-text">No hay actividad registrada en este periodo.</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Columna Derecha: Estado de Google, Cloud Functions y Dispositivos */}
                            <div className="col-right">
                                {/* Doughnut Chart para Inicios de Sesión */}
                                <div className="chart-container-facturacion">
                                    <h4>Inicios de Sesión por Dispositivo</h4>
                                    <p className="chart-subtitle">Clasificación por origen de acceso</p>
                                    {totalLogins > 0 ? (
                                        <div className="device-distribution-container">
                                            <ResponsiveContainer width="100%" height={160}>
                                                <PieChart>
                                                    <Pie
                                                        data={deviceData}
                                                        cx="50%"
                                                        cy="50%"
                                                        innerRadius={45}
                                                        outerRadius={65}
                                                        paddingAngle={3}
                                                        dataKey="value"
                                                    >
                                                        {deviceData.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={DEVICE_COLORS[index % DEVICE_COLORS.length]} />
                                                        ))}
                                                    </Pie>
                                                    <Tooltip />
                                                </PieChart>
                                            </ResponsiveContainer>
                                            <div className="device-legend">
                                                <div className="legend-row">
                                                    <span className="dot" style={{ backgroundColor: '#8b5cf6' }}></span>
                                                    <strong>App Móvil:</strong>
                                                    <span>{usoData.dispositivos.movil} ({((usoData.dispositivos.movil / totalLogins) * 100).toFixed(1)}%)</span>
                                                </div>
                                                <div className="legend-row" style={{ marginTop: '0.5rem' }}>
                                                    <span className="dot" style={{ backgroundColor: '#3b82f6' }}></span>
                                                    <strong>Navegador Web:</strong>
                                                    <span>{usoData.dispositivos.web} ({((usoData.dispositivos.web / totalLogins) * 100).toFixed(1)}%)</span>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="empty-state-donut">
                                            <p>Sin datos de inicio de sesión</p>
                                        </div>
                                    )}
                                </div>

                                {/* Estado de Cloud Functions GCP Console Style */}
                                <div className="chart-container-facturacion" style={{ marginTop: '1.5rem' }}>
                                    <h4>Estado de Cloud Functions (Servicios)</h4>
                                    <p className="chart-subtitle">Estado de salud de microservicios backend de Google Cloud</p>
                                    <div className="gcp-services-list">
                                        {usoData.funcionesEstado.map((func, i) => (
                                            <div key={i} className="gcp-service-item">
                                                <div className="service-main-info">
                                                    <span className="status-indicator-dot" style={{ backgroundColor: func.color }}></span>
                                                    <strong>{func.name}</strong>
                                                </div>
                                                <div className="service-details-badge">
                                                    {func.status === 'Saludable' ? (
                                                        <span className="badge-healthy">100% OK</span>
                                                    ) : (
                                                        <span className={func.status === 'Crítico' ? "badge-critical" : "badge-warning"}>
                                                            {func.errors} err / 24h
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Diagnóstico de Interrupciones */}
                                <div className="chart-container-facturacion" style={{ marginTop: '1.5rem' }}>
                                    <h4>Alertas de Diagnóstico y Errores</h4>
                                    <p className="chart-subtitle">Incidencias de código y plataforma</p>
                                    <div className="diagnostics-summary-card">
                                        <div className="summary-row">
                                            <span>Interrupciones Graves (24h):</span>
                                            <strong className={usoData.interrupciones > 0 ? "text-danger" : "text-success"}>
                                                {usoData.interrupciones}
                                            </strong>
                                        </div>
                                    </div>
                                    
                                    {usoData.erroresRecientes && usoData.erroresRecientes.length > 0 && (
                                        <div className="mini-errors-log" style={{ marginTop: '1rem' }}>
                                            {usoData.erroresRecientes.slice(0, 3).map((err, idx) => (
                                                <div key={idx} className={`mini-error-item severidad-${err.severidad}`}>
                                                    <strong>{err.tipo || 'Error general'}</strong>
                                                    <p>{err.mensaje}</p>
                                                    <small>{new Date(err.fecha?.toDate?.() || err.fecha).toLocaleTimeString('es-MX')}</small>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            }

            case 'experiencia': {
                return (
                    <div className="tab-container-zen">
                        <div className="section-header-zen">
                            <h3>Experiencia de Usuario (UX)</h3>
                            <p>Análisis del uso de funcionalidades del sistema e interacción de los usuarios en la interfaz.</p>
                        </div>

                        {/* KPIs de UX */}
                        <div className="kpi-facturacion-grid">
                            <div className="kpi-facturacion-card">
                                <small>Tiempo Promedio por Sesión</small>
                                <span className="valor-margen">{experienciaData.tiempoSesion}</span>
                            </div>
                            <div className="kpi-facturacion-card">
                                <small>Tasa de Rebote Promedio</small>
                                <span className="valor-positivo" style={{ color: '#10b981' }}>{experienciaData.tasaRebote}</span>
                            </div>
                            <div className="kpi-facturacion-card">
                                <small>Satisfacción Promedio (CSAT)</small>
                                <span className="valor-crecimiento">94.8%</span>
                            </div>
                            <div className="kpi-facturacion-card">
                                <small>Errores de Frontend UI (24h)</small>
                                <span className="valor-positivo">0</span>
                            </div>
                        </div>

                        <div className="charts-main-grid">
                            {/* Uso de Módulos */}
                            <div className="chart-container-facturacion">
                                <h4>Módulos Más Utilizados del Sistema</h4>
                                <p className="chart-subtitle">Frecuencia de interacciones agrupadas por sección</p>
                                {experienciaData.usoModulos.length > 0 ? (
                                    <ResponsiveContainer width="100%" height={280}>
                                        <BarChart data={experienciaData.usoModulos} layout="vertical">
                                            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eee" />
                                            <XAxis type="number" axisLine={false} tickLine={false} />
                                            <YAxis dataKey="name" type="category" width={100} axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
                                            <Tooltip />
                                            <Bar dataKey="value" fill="#6366f1" radius={[0, 4, 4, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <div className="empty-state-zen"><p>Cargando datos de interacción...</p></div>
                                )}
                            </div>

                            {/* Distribución por hora */}
                            <div className="chart-container-facturacion">
                                <h4>Actividad Horaria de los Usuarios</h4>
                                <p className="chart-subtitle">Inicios de sesión y operaciones realizadas por hora del día</p>
                                <ResponsiveContainer width="100%" height={280}>
                                    <AreaChart data={experienciaData.actividadHora}>
                                        <defs>
                                            <linearGradient id="colorHour" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.2}/>
                                                <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10 }} />
                                        <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10 }} />
                                        <Tooltip />
                                        <Area type="monotone" dataKey="cantidad" stroke="#8b5cf6" fillOpacity={1} fill="url(#colorHour)" strokeWidth={3} />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                );
            }

            case 'soporte': {
                return (
                    <div className="tab-container-zen">
                        <div className="section-header-zen">
                            <h3>Soporte y Mantenimiento (Notificaciones y Reportes)</h3>
                            <p>Supervisión global de las solicitudes de soporte levantadas desde la sección de configuraciones.</p>
                        </div>

                        {/* KPIs de Soporte */}
                        <div className="kpis-row-zen">
                            <div className="kpi-card-zen">
                                <div className="kpi-header-zen">
                                    <FaTools className="kpi-icon-blue" />
                                    <span className="kpi-title-zen">Total Reportes Creados</span>
                                </div>
                                <span className="kpi-value-zen">{soporteData.totalTickets}</span>
                                <span className="kpi-desc-zen">Histórico acumulado</span>
                            </div>

                            <div className="kpi-card-zen">
                                <div className="kpi-header-zen">
                                    <FaCheckCircle className="kpi-icon-green" />
                                    <span className="kpi-title-zen">Resueltas / Atendidas</span>
                                </div>
                                <span className="kpi-value-zen">{soporteData.resueltos}</span>
                                <span className="kpi-desc-zen">({soporteData.tasaResolucion.toFixed(1)}% de efectividad)</span>
                            </div>

                            <div className="kpi-card-zen">
                                <div className="kpi-header-zen">
                                    <FaExclamationTriangle className="kpi-icon-orange" />
                                    <span className="kpi-title-zen">Contactadas (En Proceso)</span>
                                </div>
                                <span className="kpi-value-zen">{soporteData.enProceso}</span>
                                <span className="kpi-desc-zen">En seguimiento</span>
                            </div>

                            <div className="kpi-card-zen">
                                <div className="kpi-header-zen">
                                    <FaExclamationCircle className="kpi-icon-red" />
                                    <span className="kpi-title-zen">Pendientes</span>
                                </div>
                                <span className="kpi-value-zen">{soporteData.pendientes}</span>
                                <span className="kpi-desc-zen">Sin respuesta inicial</span>
                            </div>
                        </div>

                        <div className="dashboard-two-columns" style={{ marginTop: '1.5rem' }}>
                            {/* Tickets Recientes */}
                            <div className="col-left">
                                <div className="chart-container-facturacion">
                                    <h4>Historial de Soporte Reciente</h4>
                                    <p className="chart-subtitle">Últimos tickets creados globalmente por los usuarios</p>
                                    
                                    <div className="support-tickets-list">
                                        {soporteData.ticketsRecientes && soporteData.ticketsRecientes.length > 0 ? (
                                            soporteData.ticketsRecientes.map(ticket => {
                                                const s = String(ticket.status || 'pendiente').toLowerCase();
                                                const labelEstado = s === 'resuelta' ? 'Resuelto' : (s === 'contactada' ? 'Contactado' : (s === 'rechazada' ? 'Rechazado' : 'Pendiente'));
                                                return (
                                                    <div key={ticket.id} className="support-ticket-item">
                                                        <div className="ticket-header">
                                                            <span className="badge-type badge-primary">
                                                                {ticket.categoria || 'Soporte'}
                                                            </span>
                                                            <span className={`badge-status status-${s}`}>
                                                                {labelEstado}
                                                            </span>
                                                        </div>
                                                        <strong className="ticket-title">{ticket.asunto || 'Sin asunto'}</strong>
                                                        <p className="ticket-desc">{ticket.mensaje}</p>
                                                        <div className="ticket-footer">
                                                            <span>Usuario: {ticket.nombre || 'Anónimo'} {ticket.fraccionamiento ? `(${ticket.fraccionamiento})` : ''}</span>
                                                            <small>
                                                                {(ticket.createdAt && typeof ticket.createdAt.toDate === 'function') 
                                                                    ? ticket.createdAt.toDate().toLocaleDateString('es-MX') 
                                                                    : (ticket.createdAt ? new Date(ticket.createdAt).toLocaleDateString('es-MX') : 'N/D')}
                                                            </small>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div className="empty-state-zen"><p>No hay reportes de soporte creados.</p></div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Distribución por Categoría de Ticket */}
                            <div className="col-right">
                                <div className="chart-container-facturacion">
                                    <h4>Distribución de Reportes por Categoría</h4>
                                    <p className="chart-subtitle">Relación temática de solicitudes</p>
                                    {soporteData.distribucionTipo.length > 0 ? (
                                        <div className="device-distribution-container">
                                            <ResponsiveContainer width="100%" height={160}>
                                                <PieChart>
                                                    <Pie
                                                        data={soporteData.distribucionTipo}
                                                        cx="50%"
                                                        cy="50%"
                                                        innerRadius={45}
                                                        outerRadius={65}
                                                        paddingAngle={3}
                                                        dataKey="value"
                                                    >
                                                        {soporteData.distribucionTipo.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={SUPPORT_COLORS[index % SUPPORT_COLORS.length]} />
                                                        ))}
                                                    </Pie>
                                                    <Tooltip />
                                                </PieChart>
                                            </ResponsiveContainer>
                                            <div className="device-legend">
                                                {soporteData.distribucionTipo.map((item, i) => (
                                                    <div key={i} className="legend-row" style={{ marginTop: i > 0 ? '0.5rem' : 0 }}>
                                                        <span className="dot" style={{ backgroundColor: SUPPORT_COLORS[i % SUPPORT_COLORS.length] }}></span>
                                                        <strong>{item.name}:</strong>
                                                        <span>{item.value} ({((item.value / soporteData.totalTickets) * 100).toFixed(0)}%)</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="empty-state-donut"><p>Cargando desglose de soporte...</p></div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            }

            case 'churn': {
                return (
                    <div className="tab-container-zen">
                        <div className="section-header-zen">
                            <h3>Churn Analysis (Análisis de Retención y Pérdida)</h3>
                            <p>Seguimiento de la retención de fraccionamientos afiliados, tasa de cancelación y recuperación.</p>
                        </div>

                        {/* KPIs de Churn */}
                        <div className="kpis-row-zen">
                            <div className="kpi-card-zen">
                                <div className="kpi-header-zen">
                                    <FaBuilding className="kpi-icon-blue" />
                                    <span className="kpi-title-zen">Fraccionamientos Registrados</span>
                                </div>
                                <span className="kpi-value-zen">{churnData.totalFraccs}</span>
                                <span className="kpi-desc-zen">Base instalada total</span>
                            </div>

                            <div className="kpi-card-zen">
                                <div className="kpi-header-zen">
                                    <FaCheckCircle className="kpi-icon-green" />
                                    <span className="kpi-title-zen">Activos (Suscritos)</span>
                                </div>
                                <span className="kpi-value-zen">{churnData.activos}</span>
                                <span className="kpi-desc-zen">Generando ingresos recurrentes</span>
                            </div>

                            <div className="kpi-card-zen">
                                <div className="kpi-header-zen">
                                    <FaExclamationTriangle className="kpi-icon-red" />
                                    <span className="kpi-title-zen">Inactivos / Vencidos</span>
                                </div>
                                <span className="kpi-value-zen">{churnData.inactivos}</span>
                                <span className="kpi-desc-zen">Cuentas con demo vencido o impago</span>
                            </div>

                            <div className="kpi-card-zen">
                                <div className="kpi-header-zen">
                                    <FaSignOutAlt className="kpi-icon-purple" />
                                    <span className="kpi-title-zen">Tasa de Cancelación (Churn)</span>
                                </div>
                                <span className="kpi-value-zen" style={{ color: churnData.churnRate > 10 ? '#ef4444' : '#10b981' }}>
                                    {churnData.churnRate.toFixed(1)}%
                                </span>
                                <span className="kpi-desc-zen">Meta saludable: &lt; 5.0%</span>
                            </div>
                        </div>

                        {/* Fraccionamientos Inactivos a Recuperar */}
                        <div className="vencimientos-container" style={{ marginTop: '2rem' }}>
                            <div className="section-header-zen-mini">
                                <h4>Fraccionamientos Inactivos o Vencidos (Recuperar)</h4>
                                <p>Cuentas inactivas o con periodo de suscripción expirado que requieren seguimiento.</p>
                            </div>
                            {churnData.inactivosList.length > 0 ? (
                                <div className="vencimientos-list">
                                    {churnData.inactivosList.map(fracc => (
                                        <div key={fracc.id} className="vencimiento-item inactivo-item">
                                            <div className="vencimiento-info">
                                                <strong>{fracc.nombre}</strong>
                                                <span className="badge-danger">{fracc.estado}</span>
                                            </div>
                                            <div className="vencimiento-contacto">
                                                <small>Administrador: {fracc.adminNombre}</small>
                                                <small>{fracc.adminEmail} • {fracc.adminTelefono}</small>
                                                <small>Último plan: <strong>{fracc.suscripcion}</strong></small>
                                            </div>
                                            <button className="btn-accion-zen btn-recovery" onClick={() => navigate(`/fraccionamientos/${fracc.id}`)}>
                                                Contactar y Activar
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="empty-state-zen">
                                    <p>¡Increíble! Todos los fraccionamientos se encuentran activos en la plataforma. Churn del 0%.</p>
                                </div>
                            )}
                        </div>
                    </div>
                );
            }

            case 'facturacion': {
                const totalPlanes = facturacionData.desglosePlanes.reduce((acc, curr) => acc + curr.Cantidad, 0);
                const planesPieData = facturacionData.desglosePlanes.map((p, idx) => ({
                    ...p,
                    percentage: totalPlanes > 0 ? ((p.Cantidad / totalPlanes) * 100).toFixed(0) : 0,
                    value: p.Cantidad
                }));

                return (
                    <div className="tab-container-zen">
                        {/* KPIs de Facturación */}
                        <div className="kpi-facturacion-grid">
                            <div className="kpi-facturacion-card">
                                <div className="kpi-facturacion-header">
                                    <FaFileInvoiceDollar color="#10b981" />
                                    <small>Ingresos Mensuales (MRR)</small>
                                </div>
                                <span className="valor-positivo">{formatCurrency(facturacionData.ingresosMesActual)}</span>
                            </div>
                            <div className="kpi-facturacion-card">
                                <div className="kpi-facturacion-header">
                                    <FaChartBar color="#3b82f6" />
                                    <small>Crecimiento MoM</small>
                                </div>
                                <span className="valor-crecimiento">
                                    {facturacionData.crecimientoMoM >= 0 ? '+' : ''}{facturacionData.crecimientoMoM.toFixed(1)}%
                                </span>
                            </div>
                            <div className="kpi-facturacion-card">
                                <div className="kpi-facturacion-header">
                                    <FaWallet color="#8b5cf6" />
                                    <small>Ticket Promedio (ARPU)</small>
                                </div>
                                <span className="valor-margen">{formatCurrency(facturacionData.arpu)}</span>
                            </div>
                            <div className="kpi-facturacion-card">
                                <div className="kpi-facturacion-header">
                                    <FaWallet color="#6366f1" />
                                    <small>Facturación Acumulada</small>
                                </div>
                                <span className="valor-positivo" style={{ color: '#6366f1' }}>{formatCurrency(facturacionData.ingresosTotales || 0)}</span>
                            </div>
                        </div>

                        {/* Distribución Visual de Ingresos */}
                        <div className="charts-main-grid">
                            <div className="chart-container-facturacion">
                                <h4>Histórico de Ingresos Mensuales (MRR)</h4>
                                <p className="chart-subtitle">Crecimiento acumulado facturado mes a mes</p>
                                <ResponsiveContainer width="100%" height={280}>
                                    <AreaChart data={facturacionData.ingresosPorMes}>
                                        <defs>
                                            <linearGradient id="colorIngresos" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2}/>
                                                <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                                        <XAxis dataKey="name" axisLine={false} tickLine={false} />
                                        <YAxis axisLine={false} tickLine={false} />
                                        <Tooltip formatter={(value) => formatCurrency(value)} />
                                        <Area type="monotone" dataKey="Ingresos" stroke="#6366f1" fillOpacity={1} fill="url(#colorIngresos)" strokeWidth={3} />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>

                            <div className="chart-container-facturacion">
                                <h4>Distribución por Plan de Suscripción</h4>
                                <p className="chart-subtitle">Proporción de fraccionamientos en cada nivel</p>
                                {totalPlanes > 0 ? (
                                    <div className="device-distribution-container">
                                        <ResponsiveContainer width="100%" height={160}>
                                            <PieChart>
                                                <Pie
                                                    data={planesPieData}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={45}
                                                    outerRadius={65}
                                                    paddingAngle={3}
                                                    dataKey="value"
                                                >
                                                    {planesPieData.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                    ))}
                                                </Pie>
                                                <Tooltip />
                                            </PieChart>
                                        </ResponsiveContainer>
                                        <div className="device-legend">
                                            {planesPieData.map((item, i) => (
                                                <div key={i} className="legend-row" style={{ marginTop: i > 0 ? '0.5rem' : 0 }}>
                                                    <span className="dot" style={{ backgroundColor: COLORS[i % COLORS.length] }}></span>
                                                    <strong>{item.name}:</strong>
                                                    <span>{item.Cantidad} ({item.percentage}%)</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="empty-state-donut"><p>Cargando desglose de planes...</p></div>
                                )}
                            </div>
                        </div>

                        {/* Tabla de detalle de planes */}
                        <div className="vencimientos-container" style={{ marginTop: '1.5rem' }}>
                            <div className="section-header-zen-mini">
                                <h4>Resumen Detallado de Planes</h4>
                                <p>Ingresos y clientes asociados por tipo de suscripción</p>
                            </div>
                            <div className="table-responsive-zen">
                                <table className="zen-data-table">
                                    <thead>
                                        <tr>
                                            <th>Plan de Suscripción</th>
                                            <th>Fraccionamientos Afiliados</th>
                                            <th>Porcentaje</th>
                                            <th>Ingreso Contribuido</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {facturacionData.desglosePlanes.map((p, idx) => {
                                            const totalRevPlan = facturacionData.distribucionMonetaria.find(d => d.name === p.name)?.value || 0;
                                            return (
                                                <tr key={idx}>
                                                    <td><strong>{p.name}</strong></td>
                                                    <td>{p.Cantidad} fraccs</td>
                                                    <td>{totalPlanes > 0 ? ((p.Cantidad / totalPlanes) * 100).toFixed(1) : 0}%</td>
                                                    <td><span className="valor-positivo">{formatCurrency(totalRevPlan)}</span></td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                );
            }

            case 'flujo-caja': {
                return (
                    <div className="tab-container-zen">
                        {/* KPIs de Flujo de Caja */}
                        <div className="kpi-facturacion-grid">
                            <div className="kpi-facturacion-card">
                                <div className="kpi-facturacion-header">
                                    <FaWallet color="#10b981" />
                                    <small>Ticket Promedio (ARPU)</small>
                                </div>
                                <span className="valor-positivo">{formatCurrency(facturacionData.arpu)}</span>
                            </div>
                            <div className="kpi-facturacion-card">
                                <div className="kpi-facturacion-header">
                                    <FaChartBar color="#3b82f6" />
                                    <small>Proyección Anual LTV (12m)</small>
                                </div>
                                <span className="valor-crecimiento" style={{ color: '#10b981' }}>
                                    {formatCurrency(proyeccionCalculada.proyeccionAnual)}
                                </span>
                            </div>
                            <div className="kpi-facturacion-card">
                                <div className="kpi-facturacion-header">
                                    <FaWallet color="#8b5cf6" />
                                    <small>Ingresos Mensuales actuales</small>
                                </div>
                                <span className="valor-positivo">{formatCurrency(facturacionData.ingresosMesActual)}</span>
                            </div>
                            <div className="kpi-facturacion-card">
                                <div className="kpi-facturacion-header">
                                    <FaServer color="#6366f1" />
                                    <small>Crecimiento Trimestral Proyectado</small>
                                </div>
                                <div className="growth-interactive-container">
                                    <span className="growth-sign" style={{ color: '#6366f1' }}>
                                        {crecimientoProyectado >= 0 ? '+' : ''}
                                    </span>
                                    <input 
                                        type="number" 
                                        value={crecimientoProyectado} 
                                        onChange={(e) => {
                                            const val = parseFloat(e.target.value);
                                            setCrecimientoProyectado(Number.isNaN(val) ? 0 : val);
                                        }}
                                        step="0.5"
                                        min="-50"
                                        max="100"
                                        className="growth-input-zen"
                                    />
                                    <span className="growth-percent" style={{ color: '#6366f1' }}>%</span>
                                </div>
                            </div>
                        </div>

                        <div className="charts-main-grid">
                            {/* Gráfica de Proyección */}
                            <div className="chart-container-facturacion">
                                <h4>Proyección de Ingresos (Próximos 12 Meses)</h4>
                                <p className="chart-subtitle">Simulación de crecimiento compuesto del {crecimientoProyectado}% trimestral</p>
                                <ResponsiveContainer width="100%" height={280}>
                                    <AreaChart data={proyeccionCalculada.datosProyeccion}>
                                        <defs>
                                            <linearGradient id="colorProj" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#10b981" stopOpacity={0.2}/>
                                                <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
                                        <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11 }} tickFormatter={(val) => `$${(val/1000).toFixed(1)}k`} />
                                        <Tooltip 
                                            formatter={(value) => formatCurrency(value)}
                                            contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                        />
                                        <Area type="monotone" dataKey="Proyeccion" stroke="#10b981" fillOpacity={1} fill="url(#colorProj)" strokeWidth={3} />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Gráfica de Distribución por Plan */}
                            <div className="chart-container-facturacion">
                                <h4>Distribución de Ingresos por Plan (MXN)</h4>
                                <p className="chart-subtitle">¿Qué planes aportan más al flujo recurrente?</p>
                                <ResponsiveContainer width="100%" height={280}>
                                    <PieChart>
                                        <Pie
                                            data={facturacionData.distribucionMonetaria}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={55}
                                            outerRadius={75}
                                            paddingAngle={4}
                                            dataKey="value"
                                        >
                                            {facturacionData.distribucionMonetaria.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(value) => formatCurrency(value)} />
                                        <Legend verticalAlign="bottom" height={36} tick={{ fontSize: 12 }} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* Alertas de Vencimiento */}
                        <div className="vencimientos-container">
                            <div className="section-header-zen-mini">
                                <h4>Alertas de Vencimiento de Contrato (Próximos 15 días)</h4>
                                <p>Cuentas prontas a vencer o ya expiradas. Contacta a los administradores para asegurar su continuidad.</p>
                            </div>
                            
                            {facturacionData.vencimientosProximos.length > 0 ? (
                                <div className="vencimientos-list">
                                    {facturacionData.vencimientosProximos.map(v => (
                                        <div key={v.id} className={`vencimiento-item ${v.vencido ? 'vencido-alert' : ''}`}>
                                            <div className="vencimiento-info">
                                                <strong>{v.nombre}</strong>
                                                <span>
                                                    {v.vencido ? (
                                                        <span className="texto-peligro" style={{ fontWeight: 'bold' }}>{v.diasLabel} ({v.vencimiento})</span>
                                                    ) : (
                                                        <span>Vence: <span className="texto-peligro">{v.vencimiento}</span> ({v.diasLabel})</span>
                                                    )}
                                                </span>
                                            </div>
                                            <div className="vencimiento-contacto">
                                                <small>Administrador: {v.admin}</small>
                                                <small>{v.contacto}</small>
                                            </div>
                                            <button className="btn-accion-zen" onClick={() => navigate(`/fraccionamientos/${v.id}`)}>
                                                Gestionar Suscripción
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="empty-state-zen">
                                    <FaCheckCircle className="kpi-icon-green" size={32} />
                                    <p>No hay suscripciones prontas a vencer en los siguientes 15 días. ¡Todo en orden!</p>
                                </div>
                            )}
                        </div>
                    </div>
                );
            }

            default: 
                return (
                    <div className="chart-card">
                        <h3>Selecciona una categoría</h3>
                        <p>Por favor, selecciona una categoría del menú lateral para ver las métricas correspondientes.</p>
                    </div>
                );
        }
    };
    
    return (
        <div className="metricas-layout">
            <aside className="metricas-sidebar">
                <div className="sidebar-header">
                    <PlatformBackButton className="platform-back-button" />
                    <div>
                        <h3>Dashboard Analytics</h3>
                        <p>Panel de Control Global</p>
                    </div>
                </div>
                <nav>
                    {menuItems.map((item) => (
                        <button
                            type="button"
                            key={item.key}
                            className={activeView === item.key ? 'active' : ''}
                            onClick={() => setActiveView(item.key)}
                            aria-pressed={activeView === item.key}
                            title={item.label}
                        >
                            {item.icon}
                            <span>{item.label}</span>
                        </button>
                    ))}
                </nav>
            </aside>
            <main className="metricas-content">
                {renderContent()}
            </main>
        </div>
    );
};

export default MetricasPlataforma;